import React, { useRef, useState } from "react";
import { styled } from "@mui/material/styles";
import { CircularProgress } from "@mui/material";
import { ChevronRight } from "lucide-react";
import { TriggerProps, PathSelection } from "../types";
import { useCommandDropdown } from "../context";
import { cn } from "@/helpers/utils";
import BasePopper from "@reusables/BasePopper";
import { ReactComponent as CrossSVG } from "@assets/icons/ic_cross.svg";

// Styled input wrapper matching BaseDropdown style
const InputWrapper = styled("div")(
    () => `
    width: 100%;
    border: 1px solid;
    background-color: #fff;
    border-radius: 8px;
    padding: 12px 16px;
    padding-right: 40px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;
    transition: .15s;
    position: relative;
    cursor: pointer;
    min-height: 52px;

    &.active {
        border-color: #7556FA !important;
    }

    &.disabled {
        pointer-events: none;
        background-color: #f5f5f5;
    }

    /* Compact variant, matching inputs with 8px / 12px padding */
    &.sm {
        min-height: 40px;
        padding: 8px 12px;
        padding-right: 36px;
        flex-wrap: nowrap;
    }

    & .placeholder {
        color: #D9D6DA;
        font-weight: 100;
    }
`
);

// Styled tag for selected items (multiple mode)
const StyledTag = styled("div")(
    () => `
    display: flex;
    align-items: center;
    height: 26px;
    line-height: 22px;
    background-color: #F3F2EF;
    color: #3C3769;
    border: 1px solid transparent;
    border-radius: 4px;
    box-sizing: content-box;
    padding: 0 8px;
    font-size: 14px;
    outline: 0;
    overflow: hidden;

    & span {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    & svg {
        font-size: 12px;
        margin-left: 4px;
        color: #3C3769;
    }
`
);

// Default label formatter - joins labels with " > "
function defaultGetLabel(path: PathSelection["path"], labels: string[]): string {
    if (labels.length > 0) {
        return labels.join(" > ");
    }
    // Fallback to path if no labels
    return path.map(String).join(" > ");
}

export function Trigger({
    placeholder,
    label,
    brightLabel = false,
    getLabel,
    isLoading = false,
    disabled = false,
    error,
    hideErrors = false,
    className,
    size = "md",
}: TriggerProps) {
    const { isOpen, setIsOpen, selections, multiple, getLabelForPath } = useCommandDropdown();
    const popperAnchorRef = useRef<HTMLDivElement>(null);
    const [isFocused, setIsFocused] = useState(false);

    const handleClick = () => {
        if (!disabled && !isLoading) {
            setIsOpen(true);
        }
    };

    const hasValue = selections.length > 0;

    // Get error message
    const errorMessage = typeof error === "string" ? error : error?.message;

    // Get display label for a selection. Falls back to registry-based labels when needed.
    const buildLabelsFromPath = (path: PathSelection["path"]): string[] => {
        return path.map((key, index) => {
            const prefix = path.slice(0, index + 1);
            return getLabelForPath(prefix) ?? String(key);
        });
    };

    const getLabelForSelection = (selection: PathSelection): string => {
        const resolvedLabels =
            selection.labels.length > 0 ? selection.labels : buildLabelsFromPath(selection.path);

        if (getLabel) {
            const customLabel = getLabel(selection.path, resolvedLabels);
            if (customLabel) {
                return customLabel;
            }
        }

        return defaultGetLabel(selection.path, resolvedLabels);
    };

    return (
        <div className={cn(`relative w-full`, className)} ref={popperAnchorRef}>
            {/* Label */}
            {label && (
                <p
                    className={`mb-2 font-semibold ${
                        brightLabel ? "text-inputs-label-bright" : "text-inputs-label-dim"
                    }`}
                >
                    {typeof label === "string" ? <span>{label}</span> : label}
                </p>
            )}

            {/* Input wrapper */}
            <InputWrapper
                onClick={handleClick}
                tabIndex={0}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                onMouseEnter={() => setIsFocused(true)}
                onMouseLeave={() => setIsFocused(false)}
                className={`
                    ${size === "sm" ? "sm" : ""}
                    ${isOpen ? "active" : ""}
                    ${disabled ? "disabled !border-inputs-border-disabled" : ""}
                    ${error ? "!border-inputs-border-error" : "!border-inputs-border-default"}
                `}
            >
                {/* Render selected values */}
                {hasValue ? (
                    multiple ? (
                        selections.map((selection, index) => (
                            <StyledTag key={index}>
                                <span>{getLabelForSelection(selection)}</span>
                            </StyledTag>
                        ))
                    ) : size === "sm" ? (
                        // A single line keeps compact rows aligned; the full label stays available on hover
                        <span
                            className="text-accent truncate min-w-0"
                            title={getLabelForSelection(selections[0])}
                        >
                            {getLabelForSelection(selections[0])}
                        </span>
                    ) : (
                        <span className="text-accent py-1">
                            {getLabelForSelection(selections[0])}
                        </span>
                    )
                ) : (
                    <span className="py-1 text-inputs-placeholder font-thin">{placeholder}</span>
                )}

                {/* Right icon: loading or chevron */}
                <div
                    className={cn(
                        "absolute h-full flex items-center text-dropdown-icon",
                        size === "sm" ? "right-3" : "right-4"
                    )}
                >
                    {isLoading ? (
                        <CircularProgress color="inherit" size={20} />
                    ) : (
                        <ChevronRight size={20} />
                    )}
                </div>
            </InputWrapper>

            {!hideErrors && errorMessage && (
                <BasePopper
                    anchorEl={popperAnchorRef.current}
                    open={!!errorMessage && (isFocused || isOpen)}
                    placement="bottom"
                >
                    <div
                        className="bg-[#fff] p-[14px] rounded-[8px] levitation text-inputs-textError font-thin text-sm space-y-[4px]"
                        style={{ width: popperAnchorRef.current?.offsetWidth }}
                    >
                        <div className="flex items-center">
                            <CrossSVG
                                style={{
                                    transform: "scale(.7)",
                                    marginRight: "4px",
                                    marginTop: "-1px",
                                }}
                            />
                            <span>{errorMessage}</span>
                        </div>
                    </div>
                </BasePopper>
            )}
        </div>
    );
}

export default Trigger;
